import { formatDateTime, isoAttr } from "../../lib/datetime.ts";
import { useState, type ReactNode } from "react";
import { StatusBadge, defaultNumberFormat } from "../../components/index.ts";
import type { ComplianceReference, MappedFinding } from "./use-compliance.ts";
import {
  confidenceLabel,
  confidenceStatus,
  severityBadge,
  signalLabel,
  sortMappedFindings,
} from "./vocab.ts";

/**
 * The per-reference finding-count table and its drill-down (task 10.3; design C6; Requirements 4.1,
 * 4.2). It mirrors the Posture screen's table + drill-down conventions:
 *
 * - ONE ROW per `mapping.references[]`, showing the reference id, its human title, and its
 *   `finding_count` (Requirement 4.1). The count comes straight off the server response — nothing
 *   is tallied client-side.
 * - Selecting a reference with ≥1 finding EXPANDS it in place to reveal its underlying findings
 *   (Requirement 4.2): each finding's id, its severity-or-tier badge (icon+text, never colour
 *   alone), its Oathera signal (kind + value), and the crosswalk `confidence` + `rationale` behind
 *   the mapping. A reference with no findings is not expandable (nothing to drill into).
 *
 * The table is a real semantic `<table>` with a caption, column headers and a row header per
 * reference; the expansion is a details row toggled by an `aria-expanded` button, so the drill-down
 * is keyboard-reachable and its state is announced.
 */

export interface ReferenceTableProps {
  readonly references: readonly ComplianceReference[];
}

export function ReferenceTable(props: ReferenceTableProps): ReactNode {
  // Which reference ids are currently expanded (drill-down open). Multiple may be open at once.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());

  function toggle(reference: string): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(reference)) next.delete(reference);
      else next.add(reference);
      return next;
    });
  }

  return (
    <section className="cmp-section" aria-label="Framework references">
      <div className="cmp-section__head">
        <h2 className="cmp-section__title">References</h2>
        <p className="cmp-section__note">
          One row per framework reference with its count of current mapped findings. Select a
          reference with findings to see the findings and the crosswalk rationale behind them.
        </p>
      </div>

      <div className="cmp-table__scroll">
        <table className="data-table data-table--dense" data-sticky="true">
          <caption className="sr-only">
            Framework references with the count of current mapped findings for each; a reference
            expands to its underlying findings
          </caption>
          <thead>
            <tr>
              <th scope="col">Reference</th>
              <th scope="col">Title</th>
              <th scope="col" data-align="right">
                Findings
              </th>
              <th scope="col">
                <span className="sr-only">Drill-down</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {props.references.length === 0 ? (
              <tr className="data-table__empty-row">
                <td colSpan={4}>
                  <span className="data-table__empty">
                    This framework declares no references.
                  </span>
                </td>
              </tr>
            ) : (
              props.references.map((reference) => (
                <ReferenceRow
                  key={reference.reference}
                  reference={reference}
                  isExpanded={expanded.has(reference.reference)}
                  onToggle={() => toggle(reference.reference)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ReferenceRow(props: {
  reference: ComplianceReference;
  isExpanded: boolean;
  onToggle: () => void;
}): ReactNode {
  const { reference, isExpanded } = props;
  const hasFindings = reference.finding_count > 0;

  return (
    <>
      <tr data-covered={hasFindings ? "true" : undefined}>
        <th scope="row" data-align="left">
          <span className="cmp-mono">{reference.reference}</span>
        </th>
        <td data-align="left">{reference.title}</td>
        <td data-align="right">
          {hasFindings ? (
            <span className="cmp-num cmp-num--strong">
              {defaultNumberFormat(reference.finding_count)}
            </span>
          ) : (
            <span className="cmp-zero" aria-label="none">
              n/a
            </span>
          )}
        </td>
        <td data-align="right">
          {hasFindings ? (
            <button
              type="button"
              className="cmp-link"
              aria-expanded={isExpanded}
              aria-controls={`cmp-ref-detail-${reference.reference}`}
              onClick={props.onToggle}
              aria-label={`${isExpanded ? "Hide" : "Show"} the ${defaultNumberFormat(
                reference.finding_count,
              )} findings mapped to ${reference.reference}`}
            >
              {isExpanded ? "Hide findings" : "Show findings"}
            </button>
          ) : (
            <span className="cmp-faint">Nothing mapped</span>
          )}
        </td>
      </tr>

      {hasFindings && isExpanded && (
        <tr className="cmp-detail-row">
          <td colSpan={4} id={`cmp-ref-detail-${reference.reference}`}>
            <ReferenceFindings
              reference={reference.reference}
              findings={reference.findings}
            />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * The drill-down for one reference: every underlying mapped finding, ordered by crosswalk
 * confidence then severity then recency. Each row shows the finding id, its severity-or-tier badge,
 * its Oathera signal, the crosswalk confidence badge, and the one-line rationale (Requirement 4.2).
 */
function ReferenceFindings(props: {
  reference: string;
  findings: readonly MappedFinding[];
}): ReactNode {
  const rows = sortMappedFindings(props.findings);

  return (
    <div className="cmp-detail">
      <table className="data-table data-table--dense cmp-detail__table">
        <caption className="sr-only">
          Findings mapped to {props.reference}, with the crosswalk confidence and rationale behind
          each mapping
        </caption>
        <thead>
          <tr>
            <th scope="col">Finding</th>
            <th scope="col">Severity / tier</th>
            <th scope="col">Signal</th>
            <th scope="col">Confidence</th>
            <th scope="col">Rationale</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((finding) => {
            const badge = severityBadge(finding);
            return (
              <tr key={finding.finding_id}>
                <th scope="row" data-align="left">
                  <span className="cmp-mono">{finding.finding_id}</span>
                  {finding.timestamp != null && (
                    <time
                      className="cmp-faint cmp-detail__time"
                      dateTime={isoAttr(finding.timestamp)}
                    >
                      {formatDateTime(finding.timestamp)}
                    </time>
                  )}
                </th>
                <td data-align="left">
                  <StatusBadge
                    level={badge.level}
                    label={badge.label}
                    title={badge.title}
                    size="sm"
                  />
                </td>
                <td data-align="left">
                  <span className="cmp-faint">{signalLabel(finding.signal)}</span>
                </td>
                <td data-align="left">
                  <StatusBadge
                    level={confidenceStatus(finding.confidence)}
                    label={confidenceLabel(finding.confidence)}
                    title={`Crosswalk confidence: ${confidenceLabel(
                      finding.confidence,
                    ).toLowerCase()}.`}
                    size="sm"
                  />
                </td>
                <td data-align="left">
                  <span className="cmp-rationale">{finding.rationale}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
